import { type Dispatch, type SetStateAction, useEffect } from 'react';
import { NODE_DRAG_START, NODE_DRAG_END } from '@/pages/editor/CustomEventHandler';

const EDGE_MARGIN = 80;
const MAX_SPEED = 18;

function getDeltas(
  viewport: { left: number; top: number; width: number; height: number },
  clientX: number,
  clientY: number,
): { deltaX: number; deltaY: number } {
  const { left, top, width, height } = viewport;
  const right = left + width;
  const bottom = top + height;
  let deltaX = 0;
  let deltaY = 0;

  if (clientY < top) {
    deltaY = -MAX_SPEED;
  } else if (clientY <= top + EDGE_MARGIN) {
    const t = 1 - (clientY - top) / EDGE_MARGIN;
    deltaY = -MAX_SPEED * t;
  } else if (clientY > bottom) {
    deltaY = MAX_SPEED;
  } else if (clientY >= bottom - EDGE_MARGIN) {
    const t = (clientY - (bottom - EDGE_MARGIN)) / EDGE_MARGIN;
    deltaY = MAX_SPEED * t;
  }

  if (clientX < left) {
    deltaX = -MAX_SPEED;
  } else if (clientX <= left + EDGE_MARGIN) {
    const t = 1 - (clientX - left) / EDGE_MARGIN;
    deltaX = -MAX_SPEED * t;
  } else if (clientX > right) {
    deltaX = MAX_SPEED;
  } else if (clientX >= right - EDGE_MARGIN) {
    const t = (clientX - (right - EDGE_MARGIN)) / EDGE_MARGIN;
    deltaX = MAX_SPEED * t;
  }

  return { deltaX, deltaY };
}

/**
 * Pans the canvas when dragging a node and the cursor is near (or past) the viewport edges.
 * rAF loop only runs while the cursor is in an edge zone.
 */
export function useNodeDragAutoScroll({
  frameRef,
  setTranslateX,
  setTranslateY,
}: {
  frameRef: HTMLDivElement | null;
  setTranslateX: Dispatch<SetStateAction<number>>;
  setTranslateY: Dispatch<SetStateAction<number>>;
}): void {
  useEffect(() => {
    if (!frameRef) return;
    const lastPos = { clientX: 0, clientY: 0 };
    const lastRect = { left: 0, top: 0, width: 0, height: 0 };
    let rafId: number | null = null;

    const tick = () => {
      const { deltaX, deltaY } = getDeltas(lastRect, lastPos.clientX, lastPos.clientY);
      if (deltaX !== 0 || deltaY !== 0) {
        setTranslateX((prev) => prev - deltaX);
        setTranslateY((prev) => prev - deltaY);
        rafId = window.requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    };

    const handleDragover = (e: DragEvent) => {
      lastPos.clientX = e.clientX;
      lastPos.clientY = e.clientY;
      if (rafId === null) {
        rafId = window.requestAnimationFrame(tick);
      }
    };

    const handleNodeDragStart = () => {
      const rect = frameRef.getBoundingClientRect();
      lastRect.left = rect.left;
      lastRect.top = rect.top;
      lastRect.width = rect.width;
      lastRect.height = rect.height;
      document.addEventListener('dragover', handleDragover, { passive: true });
    };

    const handleNodeDragEnd = () => {
      document.removeEventListener('dragover', handleDragover);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    window.addEventListener(NODE_DRAG_START, handleNodeDragStart);
    window.addEventListener(NODE_DRAG_END, handleNodeDragEnd);
    return () => {
      window.removeEventListener(NODE_DRAG_START, handleNodeDragStart);
      window.removeEventListener(NODE_DRAG_END, handleNodeDragEnd);
      document.removeEventListener('dragover', handleDragover);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [frameRef, setTranslateX, setTranslateY]);
}
